// Converts a verification rule (CONDITION where/update shapes from condition_rule.js) into a
// plain-English description for non-technical dashboard viewers. Deterministic — no LLM call —
// so every operator/update-type this app actually supports (see OPERATORS/UPDATE_TYPES in
// config_model.js) needs a template here rather than falling back silently.
import { getOperatorMeta, CUSTOMER_SUB_TABLES } from "./config_model";

const humanize = key =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());

const VAL_OP_LABEL = { GTE: "at least", GT: "more than", LTE: "at most", LT: "less than" };
const ARITH_OP_LABEL = { ADD: "plus", SUB: "minus", MUL: "times", DIV: "divided by" };

const describeCondition = (fieldLabel, op, cond) => {
  const val = cond.val;
  switch (op) {
    case "INCLUDES": {
      const vals = (Array.isArray(val) ? val : [val]).filter(v => v !== undefined && v !== null);
      return vals.length > 1 ? `${fieldLabel} is one of: ${vals.join(", ")}` : `${fieldLabel} includes ${vals[0]}`;
    }
    case "NOT_INCLUDES": {
      const vals = Array.isArray(val) ? val : [val];
      if (vals.some(v => v === "" || v === null || v === undefined)) return `${fieldLabel} is not empty`;
      return vals.length > 1 ? `${fieldLabel} is none of: ${vals.join(", ")}` : `${fieldLabel} does not include ${vals[0]}`;
    }
    case "LIKE":
      return `${fieldLabel} matches pattern "${val}"`;
    case "GTE":
      return `${fieldLabel} is greater than or equal to ${val}`;
    case "GT":
      return `${fieldLabel} is greater than ${val}`;
    case "LTE":
      return `${fieldLabel} is less than or equal to ${val}`;
    case "LT":
      return `${fieldLabel} is less than ${val}`;
    case "HOURS_BEFORE":
      return `${fieldLabel} is older than ${val} hour${val === 1 ? "" : "s"}`;
    case "TIME_AFTER":
      return `${fieldLabel} is after ${val}`;
    case "TIME_BEFORE":
      return `${fieldLabel} is before ${val}`;
    case "TIME_BETWEEN":
      return `${fieldLabel} is between ${(val && val[0]) || "?"} and ${(val && val[1]) || "?"}`;
    default:
      if (op && op.indexOf("TIME_DIFF") === 0) {
        const suffix = op.replace("TIME_DIFF_", "");
        return `Time gap between ${fieldLabel} and ${cond.col2 || "?"} is ${VAL_OP_LABEL[suffix] || suffix} ${val} ${cond.unit || "hours"}`;
      }
      if (op === "ARITHMATIC") {
        const combineOp = ARITH_OP_LABEL[cond.cols_op] || cond.cols_op || "combined with";
        return `${fieldLabel} ${combineOp} ${(cond.cols || []).join(", ")} is ${VAL_OP_LABEL[cond.val_op] || cond.val_op || ""} ${val}`;
      }
      return `${fieldLabel} matches a condition (${(getOperatorMeta(op) || {}).label || op || "unspecified"})`;
  }
};

const conditionsFromGroup = (group, fieldLabelFor) => Object.keys(group || {}).map(key => describeCondition(fieldLabelFor(key), group[key].op, group[key]));

const describeAction = (key, action, data) => {
  const ruleDb = data.db;
  let fieldLabel;
  if (ruleDb === "form_response_data") fieldLabel = `Input #${key}`;
  else if (ruleDb === "customer_leads") {
    const subtable = CUSTOMER_SUB_TABLES.find(t => t.value === Number(action.db)) || CUSTOMER_SUB_TABLES[0];
    fieldLabel = `${humanize(key)} (${subtable.label})`;
  } else fieldLabel = humanize(key);

  const type = Number(action.type ?? 0);
  let desc;
  if (type === 0) desc = `Set ${fieldLabel} to ${action.val}`;
  else if (type === 1) desc = `Set ${fieldLabel} from report column(s): ${(action.val || []).join(", ")}`;
  else if (type === 2) desc = `Build ${fieldLabel} from ${(action.cols || []).join(", ")}`;
  else if (type === 3) desc = `Build ${fieldLabel} from ${(action.cols || []).join(", ")} using a value mapping`;
  else desc = `Update ${fieldLabel}`;

  if (action.previousValues && action.previousValues.length) desc += ` (only if currently: ${action.previousValues.join(", ")})`;
  return desc;
};

// Categorized strictly by which task-status field the update writes to (business rule, not a
// guess from condition wording): status=1 or any status2 write -> approval, status=2 -> rejection,
// status_comment/remark -> comment. A rule can carry more than one (e.g. approval + comment
// together). Only applies to the default "Task Status" update target (data.db unset) — customer
// record / form-input updates don't carry this meaning, so they're always "other".
const inferCategories = data => {
  if (data.db) return ["other"];
  const categories = new Set();
  Object.keys(data.update || {}).forEach(key => {
    const action = data.update[key];
    if (key === "status") {
      const val = Number(action.val);
      if (val === 1) categories.add("approval");
      else if (val === 2) categories.add("rejection");
    } else if (key === "status2") {
      categories.add("approval");
    } else if (key === "status_comment" || key === "remark") {
      categories.add("comment");
    }
  });
  return categories.size ? Array.from(categories) : ["other"];
};

export const describeRule = wrapper => {
  if (wrapper._kind === "CONDITION") {
    const data = wrapper.data || {};
    const conditions = [
      ...conditionsFromGroup(data.where, key => key),
      ...conditionsFromGroup(data.db_where, key => `Task's ${humanize(key)}`),
      ...conditionsFromGroup(data.input_where, key => `Input #${key}`)
    ];
    const updateKeys = Object.keys(data.update || {});
    const action = updateKeys.length ? updateKeys.map(key => describeAction(key, data.update[key], data)).join("; ") : "No update specified";
    return { conditions, action, categories: inferCategories(data) };
  }
  if (wrapper._kind === "CAPTURE_MULTIPLE") {
    const d = wrapper.data || {};
    return {
      conditions: [d.identifier && d.identifier.col ? `Report row identifies a repeat submission via "${d.identifier.col}"` : "Report row matches an existing submission"],
      action: `Auto-capture as a repeat submission${d.unique && d.unique.col ? ` (tracked by "${d.unique.col}")` : ""}${d.unique && d.unique.input_id ? `, updates Input #${d.unique.input_id}` : ""}`,
      categories: ["other"]
    };
  }
  if (wrapper._kind === "CAPTURE_DATA") {
    const d = wrapper.data || {};
    return {
      conditions: [d.codeKey ? `Report row has a referral code in "${d.codeKey}"` : "Report row has a referral code"],
      action: "Auto-capture as a referral lead",
      categories: ["other"]
    };
  }
  return { conditions: [], action: "Unrecognized rule — view the JSON for details", categories: ["other"] };
};

export const describeConfigPlainEnglish = config => ({
  rules: (config.rules || []).map(describeRule),
  mail_routing: (config.mappings || []).map(m => ({ email: m.email_id, forms: m.form_ids || [] }))
});

export const CATEGORY_META = {
  approval: { label: "Approval", color: "#28a745", bg: "#eaf6ec" },
  rejection: { label: "Rejection", color: "#dc3545", bg: "#fdecea" },
  comment: { label: "Comment", color: "#6f42c1", bg: "#f1ebfa" },
  other: { label: "Other", color: "#6c757d", bg: "#f0f2f5" }
};
